import { Injectable } from '@angular/core';
import { Cart, OrderEntry } from '@spartacus/cart/base/root';
import {
  EcommerceProductItemDetail,
  EcommerceProductItem,
  ProductItem,
  ProductCartItemPromotionData
} from './cart/CartEvents.model';
import {
  EcommerceProductDetailItem,
  ProductDetailItem,
  ProductImpressionItem
} from './product/custom-product-events.model';
import { Order, ReplenishmentOrder } from "@spartacus/order/root";

@Injectable({
  providedIn: 'root',
})
export class TagManagerFeatureService {

  constructor() {
  }

  getOrderEntries(entries: Array<OrderEntry>): ProductItem[] {
    const entryList = entries.map((entry: OrderEntry) => {
      const gtmProduct = this.getProductItem(entry?.product, entry?.basePrice);
      return {
        ...gtmProduct,
        quantity: entry.quantity,
        coupon: '',
      };
    });

    return entryList;
  }

  getProductItem(product, basePrice): ProductDetailItem {
    return {
      name: product?.name,
      id: product?.code,
      price: basePrice?.value?.toString(),
      brand: product?.brands ? this.getEntryBrands(product?.brands) : '',
      category: product?.categories ? this.getEntryCategories(product?.categories) : '',
      variant: '',
    };
  }

  getProductImpressionItem(product, index): ProductImpressionItem {
    const position = index + 1;
    return {
      item_name: product?.name,
      item_id: product?.code,
      discount: this.getDiscount(product),
      index: position.toString(),
      item_brand: product.manufacturer ?? '',
      item_category: product.categories ? product.categories[0]?.name || '' : '',
      item_category2: product.categories ? product.categories[1]?.name || '' : '',
      item_variant: '',
      price: this.toPriceFormatted(product?.price?.value),
      promotion_id: '',
      promotion_name: '',
      quantity: 1
    }
  }

  getProductSelectedItem(product): ProductImpressionItem {
    return {
      item_name: product?.name,
      item_id: product?.code,
      coupon: '',
      discount: this.getDiscount(product),
      index: "1",
      item_brand: product.manufacturer ?? '',
      item_category: product.categories ? product.categories[0]?.name || '' : '',
      item_category2: product.categories ? product.categories[1]?.name || '' : '',
      item_variant: '',
      price: this.toPriceFormatted(product?.price?.value),
      quantity: 1,
    }
  }

  getBaseEcommerceProductItem(product, entry?): EcommerceProductItemDetail {
    const promotionProduct = this.getPromotionProductPrice(entry)
    return {
      item_name: product?.name ?? '',
      item_id: product?.code ?? '',
      item_brand: product.manufacturer ?? '',
      item_category: product.categories ? product.categories[0]?.name || '' : '',
      item_category2: product.categories ? product.categories[1]?.name || '' : '',
      item_variant: '',
      price: promotionProduct ? promotionProduct.price : this.toPriceFormatted(product?.price?.value),
      discount: promotionProduct ? promotionProduct.discount : this.getDiscount(product),
    }
  }

  getEcommerceProductDetailItem(product): EcommerceProductDetailItem {
    const ecommerceProduct = this.getBaseEcommerceProductItem(product);
    return {
      ...ecommerceProduct,
      coupon: '',
      index: '1',
      item_list_id: product.categories ? product.categories[product.categories.length - 1]?.code || '' : '',
      item_list_name: product.categories ? product.categories[product.categories.length - 1]?.name || '' : '',
      quantity: 1
    }
  }

  getCartModifiedProductItem(cart: Cart, entry, modifiedQuantity): EcommerceProductItem {
    const product = entry.product;
    const position = entry.entryNumber + 1
    const ecommerceProduct = this.getBaseEcommerceProductItem(product);
    return {
      ...ecommerceProduct,
      coupon: '',
      index: position.toString(),
      item_list_id: product.categories ? product.categories[product.categories.length - 1]?.code || '' : '',
      item_list_name: product.categories ? product.categories[product.categories.length - 1]?.name || '' : '',
      quantity: modifiedQuantity,
    }
  }

  getCartProductItem(cart: Cart, entry, modifiedQuantity): EcommerceProductItem {
    const product = entry.product
    const ecommerceProduct = this.getBaseEcommerceProductItem(product, entry);
    return {
      ...ecommerceProduct,
      quantity: modifiedQuantity,
    }
  }

  getPurchasedProductItems(order: Order | ReplenishmentOrder): EcommerceProductItem[] {
    const entries = order.entries
    return entries.map((entry: OrderEntry) => {
      const gtmEcommerceProduct = this.getBaseEcommerceProductItem(entry.product, entry);
      return {
        ...gtmEcommerceProduct,
        quantity: entry.quantity,
      };
    });
  }

  getCartModifiedEventValue(product, quantity): number {
    const price = product.price.value;
    return parseFloat((price * quantity).toFixed(2))
  }

  getEntryCategories(categories): string {
    const categoryList = categories.filter((category) => category.code.includes('nav_'));
    return categoryList.map(category => category.name).join(',');
  }

  getEntryBrands(brands): string {
    return brands.map(brand => brand.name).join(',');
  }

  getDiscount(product): number {
    const discount = product?.price?.discountValue?.value ?? 0
    return this.toPriceFormatted(discount)
  }

  toPriceFormatted(priceValue): number {
    const value = priceValue ?? 0;
    return parseFloat(value.toFixed(2));
  }

  getPromotionProductPrice(entry: OrderEntry) {
    if (!entry) {
      return undefined
    }
    // Return free product
    if (entry.totalPrice.value == 0) {
      return {price: this.toPriceFormatted(0), discount: entry.product?.price.value ?? 0};
    }

    // For checking product with promotion
    const basePrice = entry.basePrice?.value ?? 0;
    const finalEachPrice = this.toPriceFormatted(entry.totalPrice.value / entry.quantity) ?? 0;
    const eachDiscount = this.toPriceFormatted(basePrice - finalEachPrice);
    return {price: finalEachPrice, discount: eachDiscount};
  }

  // TODO: Need to be determined about the coupon and promotion data
  getOrderPromotionName(cart: Cart): string {
    if (cart && cart.appliedOrderPromotions && cart.appliedOrderPromotions.length) {
      return cart.appliedOrderPromotions[0].promotion.code
    }
    return '';
  }

  getEcommerceProductPromotionData(cart, orderEntry: OrderEntry): ProductCartItemPromotionData | undefined {
    if (cart && cart.appliedProductPromotions && cart.appliedProductPromotions.length) {
      return cart.appliedProductPromotions
        .filter(entry => entry.consumedEntries[0].orderEntryNumber === orderEntry.entryNumber)
        .map(promotion => {
          const discountValue = (orderEntry.basePrice.value - promotion.consumedEntries[0].adjustedUnitPrice);
          return <ProductCartItemPromotionData>{
            coupon: promotion.promotion.code,
            discount: parseFloat((discountValue).toFixed(2))
          }
        })[0];
    }
    return undefined;
  }
}
